/**
 * docQuery — Documents_On_Site Prompt 13-A + R-131-RAG-A hybrid retrieval.
 *
 * v1 keyword retrieval upgraded to hybrid: vector top-K (Firestore /
 * in-memory) + keyword rerank + relevance classifier boost. Keyword-only
 * path remains the fallback when embeddings aren't ready on newly-uploaded
 * docs.
 */
import type { SiteDocument } from '../types/siteRag';
import { isLibraryIndexDoc } from './knowledge/libraryIndexDocument';
import { embedText, type EmbedTextOptions } from './embeddings/embedText';
import {
  aggregateVectorHitsByDoc,
  chunksFromSiteDocs,
  DEFAULT_VECTOR_TOP_K,
  findNearestChunks,
} from './rag/vectorRetrieval';
import type { DocumentChunk } from './rag/types';

/** Tokenise a free-form query into lowercase keyword stems. Strips
 *  stopwords + punctuation; bounds at 12 tokens so a wall of text
 *  doesn't blow up scoring. */
const STOPWORDS = new Set([
  'the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'do', 'does', 'did', 'have', 'has', 'had', 'will', 'would', 'should',
  'this', 'that', 'these', 'those', 'and', 'or', 'but', 'of', 'in',
  'on', 'at', 'to', 'for', 'with', 'by', 'as', 'from', 'about', 'what',
  'how', 'when', 'where', 'why', 'who', 'which', 'i', 'we', 'you',
  'they', 'it', 'its', 'our', 'us', 'my', 'me', 'any', 'all', 'some',
  'tell', 'me', 'say', 'says', 'said', 'show', 'me', 'find', 'can',
  'could',
]);

export function tokenize(query: string): string[] {
  if (!query) return [];
  return query
    .toLowerCase()
    .replace(/[^\w\s'-]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length >= 3)
    .filter(t => !STOPWORDS.has(t))
    .slice(0, 12);
}

/** Tokenise section body text for audit keyword overlap (R-62). */
export function tokenizeSectionText(text: string, maxTokens = 512): string[] {
  if (!text) return [];
  return text
    .toLowerCase()
    .replace(/[^\w\s'-]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length >= 2)
    .filter((t) => !STOPWORDS.has(t))
    .slice(0, maxTokens);
}

/**
 * Topic-cluster synonyms for query expansion. The keyword scorer only
 * matches literal token overlap, so a "cost" query never reaches a doc
 * titled "PURCHASE PRICE …". Expanding the query (asymmetrically — the
 * doc's tokens are NOT expanded) lets semantically-adjacent terms hit.
 *
 * Deliberately small + hand-curated, not a full NLP/stemmer pipeline:
 * a handful of clusters that mattered in production triage. Keys and
 * values are lowercased; multi-char tokens like "$/kw" survive the
 * tokenizer's `\w` filter only when matched against pre-tokenize text,
 * so we keep entries to alnum tokens plus the bare "$" sentinel.
 */
export const QUERY_SYNONYMS: Record<string, string[]> = {
  // Cost / price / acquisition cluster
  cost: ['price', 'pricing', 'value', 'purchase', 'acquisition', 'capex', 'fee', 'payment', '$'],
  costs: ['price', 'pricing', 'purchase', 'acquisition', 'capex', 'fee'],
  price: ['cost', 'pricing', 'value', 'purchase', 'capex', '$'],
  pricing: ['cost', 'price', 'purchase'],
  capex: ['cost', 'price', 'capital', 'investment'],
  acquisition: ['purchase', 'acquire', 'buy', 'option', 'agreement', 'transaction', 'cost'],
  purchase: ['cost', 'price', 'acquisition', 'agreement', 'buy'],
  // Build / construction cluster
  build: ['construct', 'construction', 'epc', 'develop', 'development', 'erection'],
  construction: ['build', 'construct', 'epc', 'develop'],
  // Permitting cluster
  permit: ['license', 'authorization', 'approval', 'permitting', 'nepa', 'ceqa', 'esa'],
  permitting: ['permit', 'license', 'authorization', 'approval'],
  license: ['permit', 'authorization', 'approval'],
  // Power cluster
  power: ['electric', 'electricity', 'megawatt', 'kilowatt', 'utility', 'load', 'demand'],
  electricity: ['power', 'electric', 'megawatt', 'utility'],
  // Water cluster
  water: ['hydrology', 'aquifer', 'npdes', 'discharge', 'cooling', 'consumption', 'wastewater'],
  // Flood / environmental cluster
  flood: ['floodplain', 'flooding', 'fema', 'inundation', 'flood-zone'],
  species: ['endangered', 'threatened', 'esa', 'wildlife', 'habitat', 'fauna'],
  // Grid / interconnection cluster
  grid: ['substation', 'transmission', 'interconnection', 'utility', 'feeder'],
  interconnection: ['grid', 'substation', 'transmission', 'utility'],
};

/**
 * Expand the raw query string with topic-cluster synonyms before the
 * keyword scorer runs. Asymmetric — only query tokens add synonyms; a
 * doc's words are never expanded (a 200-token expansion would pollute
 * every doc's score). Returns a deduped space-joined string that feeds
 * straight into {@link tokenize}.
 */
export function expandQuery(query: string): string {
  if (!query) return '';
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  const expanded = new Set<string>(tokens);
  for (const tok of tokens) {
    for (const syn of QUERY_SYNONYMS[tok] ?? []) {
      expanded.add(syn.toLowerCase());
    }
  }
  return Array.from(expanded).join(' ');
}

export interface DocQueryHit {
  doc: SiteDocument;
  score: number;
  /** R-131-RAG-C — keyword component before vector scaling (0 when vector-only). */
  keywordScore?: number;
  /** R-131-RAG-C — best chunk cosine similarity when vector path ran. */
  vectorSimilarity?: number;
  /** Up to 2 short snippets pulled from textContent / summary that
   *  matched the query. Used for inline citation chips. */
  snippets: string[];
}

/** Scale cosine similarity (0..1) to sit alongside keyword scores. */
export const VECTOR_SCORE_SCALE = 100;

export interface ScoreDocsOptions {
  /** Tenant uid for Firestore findNearest scoping. */
  uid?: string;
  /** Pre-loaded chunk corpus (panel-local or test fixtures). */
  chunks?: DocumentChunk[];
  /** Override query embedding (tests). Defaults to Vertex RETRIEVAL_QUERY. */
  embedQuery?: (text: string, opts?: EmbedTextOptions) => Promise<number[]>;
  vectorTopK?: number;
  /** Force keyword-only path (tests / embedding outage). */
  keywordOnly?: boolean;
}

/**
 * Score every doc in `docs` against `query`. Pure / synchronous keyword
 * path — kept for tests and as the fallback when embeddings aren't ready.
 */
export function scoreDocsForQueryKeyword(docs: SiteDocument[], query: string): DocQueryHit[] {
  const tokens = tokenize(expandQuery(query));
  if (tokens.length === 0) return [];
  const out: DocQueryHit[] = [];
  for (const doc of docs) {
    if (isLibraryIndexDoc(doc)) continue;
    const fileName = (doc.fileName ?? '').toLowerCase();
    const tagsText = (doc.tags ?? []).join(' ').toLowerCase();
    const summary = (doc.summary ?? '').toLowerCase();
    const text = (doc.textContent ?? '').toLowerCase();
    let score = 0;
    const snippets: string[] = [];
    for (const tok of tokens) {
      if (fileName.includes(tok)) score += 5;
      if (tagsText.includes(tok)) score += 3;
      if (summary.includes(tok)) {
        score += 2;
        if (snippets.length < 2) snippets.push(extractSnippet(doc.summary ?? '', tok));
      }
      if (text.includes(tok)) {
        score += 1;
        if (snippets.length < 2) snippets.push(extractSnippet(doc.textContent ?? '', tok));
      }
    }
    if (score === 0) continue;
    if (doc.relevance && Object.keys(doc.relevance).length > 0) score = score * 1.25;
    out.push({ doc, score, keywordScore: score, snippets });
  }
  out.sort((a, b) => b.score - a.score);
  return out;
}

/**
 * R-131-RAG-A — hybrid retrieval: vector top-K + keyword rerank +
 * relevance classifier boost. Falls back to {@link scoreDocsForQueryKeyword}
 * when no embeddings are available (newly-uploaded docs).
 */
export async function scoreDocsForQuery(
  docs: SiteDocument[],
  query: string,
  options: ScoreDocsOptions = {},
): Promise<DocQueryHit[]> {
  const keywordHits = scoreDocsForQueryKeyword(docs, query);
  if (options.keywordOnly) return keywordHits;

  const docIds = docs.map(d => d.id);
  const chunkCorpus = [
    ...(options.chunks ?? []),
    ...chunksFromSiteDocs(docs, options.uid),
  ];
  const hasEmbeddings = chunkCorpus.some(c => c.vectorEmbedding?.length);
  if (!hasEmbeddings && !options.uid) return keywordHits;

  const embedFn = options.embedQuery ?? ((text: string) => embedText(text, { taskType: 'RETRIEVAL_QUERY' }));
  const queryVector = await embedFn(query);
  if (queryVector.length === 0) return keywordHits;

  const vectorHits = await findNearestChunks({
    uid: options.uid ?? '',
    queryVector,
    docIds,
    chunks: chunkCorpus,
    limit: options.vectorTopK ?? DEFAULT_VECTOR_TOP_K,
  });
  if (vectorHits.length === 0) return keywordHits;

  const vectorByDoc = aggregateVectorHitsByDoc(vectorHits);
  const keywordByDoc = new Map(keywordHits.map(h => [h.doc.id, h]));
  const candidateIds = new Set<string>([
    ...keywordHits.map(h => h.doc.id),
    ...vectorByDoc.keys(),
  ]);

  const merged: DocQueryHit[] = [];
  for (const docId of candidateIds) {
    const doc = docs.find(d => d.id === docId);
    if (!doc || isLibraryIndexDoc(doc)) continue;

    const keyword = keywordByDoc.get(docId);
    const vector = vectorByDoc.get(docId);
    let score = (keyword?.score ?? 0) + (vector?.similarity ?? 0) * VECTOR_SCORE_SCALE;

    const snippets = [...(keyword?.snippets ?? [])];
    if (snippets.length < 2 && vector?.chunk.text) {
      snippets.push(extractSnippet(vector.chunk.text, query.split(/\s+/)[0] ?? query));
    }

    if (score === 0) continue;
    if (doc.relevance && Object.keys(doc.relevance).length > 0) score *= 1.25;
    merged.push({
      doc,
      score,
      keywordScore: keyword?.score ?? 0,
      vectorSimilarity: vector?.similarity,
      snippets: snippets.slice(0, 2),
    });
  }

  merged.sort((a, b) => b.score - a.score);
  return merged.length > 0 ? merged : keywordHits;
}

/** Return a ~120-char snippet centred on the first occurrence of `term`. */
function extractSnippet(haystack: string, term: string): string {
  const idx = haystack.toLowerCase().indexOf(term.toLowerCase());
  if (idx < 0) return haystack.slice(0, 120);
  const start = Math.max(0, idx - 50);
  const end = Math.min(haystack.length, idx + term.length + 70);
  const prefix = start > 0 ? '…' : '';
  const suffix = end < haystack.length ? '…' : '';
  return `${prefix}${haystack.slice(start, end).trim()}${suffix}`;
}
